import React from 'react';
import { ICellRendererParams } from '@ag-grid-enterprise/all-modules';
import ReactDiffViewer, { DiffMethod } from 'react-diff-viewer';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faTimes, faSpinner, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { IAgentQARunResult, IAgentQAGroupedItem } from 'types/agentQA';

interface SegmentDiff {
  index: number;
  actual: string;
  expected: string;
}

interface FailedDialogSegmentsProps {
  actualSegments: any[];
  expectedResult: any;
  dialectCode: string;
}

const FailedDialogSegments: React.FC<FailedDialogSegmentsProps> = ({ actualSegments, expectedResult, dialectCode }) => {
  // Get expected segments for comparison
  let expectedSegments: any[] = [];
  try {
    const expectedObj = typeof expectedResult === 'string' ? JSON.parse(expectedResult) : expectedResult;
    expectedSegments = expectedObj?.segments || [];
  } catch (e) {
    // Ignore parse errors
  }

  // Find which segments failed (don't match expected)
  const failedSegments: SegmentDiff[] = [];
  actualSegments.forEach((actualSeg: any) => {
    const expectedSeg = expectedSegments.find((e: any) => e.index === actualSeg.index);
    if (expectedSeg && actualSeg.translation !== expectedSeg.translation) {
      failedSegments.push({
        index: actualSeg.index,
        actual: actualSeg.translation,
        expected: expectedSeg.translation,
      });
    }
  });

  // If we can't determine which segments failed, show count
  if (failedSegments.length === 0) {
    return (
      <FailureMessage>
        {actualSegments.length} segments failed validation
      </FailureMessage>
    );
  }

  // Show failed segments with diff views
  return (
    <>
      <FailureHeader>
        {failedSegments.length} of {actualSegments.length} segments failed:
      </FailureHeader>

      {failedSegments.map((seg, idx) => (
        <SegmentContainer key={seg.index} isLast={idx === failedSegments.length - 1}>
          <SegmentHeader>Segment {seg.index}:</SegmentHeader>
          <DialectLabel>{dialectCode}:</DialectLabel>
          <DiffContainer>
            <ReactDiffViewer
              oldValue={seg.expected}
              newValue={seg.actual}
              showDiffOnly
              compareMethod={DiffMethod.WORDS}
              splitView={false}
              hideLineNumbers
              styles={{
                wordDiff: {
                  display: 'inline',
                },
                diffRemoved: {
                  display: 'none',
                },
                diffAdded: {
                  display: 'none',
                },
                content: {
                  display: 'block!important',
                  fontSize: 12,
                },
                diffContainer: {
                  overflow: 'hidden',
                },
                line: {
                  wordBreak: 'break-word',
                },
              }}
            />
          </DiffContainer>
          <ExpectedText>Expected: {seg.expected}</ExpectedText>
        </SegmentContainer>
      ))}
    </>
  );
};

type CitedExample = { source: string; target: string; translation_id: number };

function normalizeCitedExamples(actual_result: any): CitedExample[] {
  try {
    const actualObj = typeof actual_result === 'string' ? JSON.parse(actual_result) : actual_result;
    const raw: any[] = Array.isArray(actualObj?.cited_examples) ? actualObj.cited_examples : [];
    const seen = new Set<string>();
    const result: CitedExample[] = [];
    for (const ex of raw) {
      const entry: CitedExample =
        typeof ex === 'string'
          ? { source: ex, target: '', translation_id: 0 }
          : { source: String(ex?.source ?? ''), target: String(ex?.target ?? ''), translation_id: Number(ex?.translation_id ?? 0) };
      if (!seen.has(entry.source)) {
        seen.add(entry.source);
        result.push(entry);
      }
    }
    return result;
  } catch {
    return [];
  }
}

interface SingleResultProps {
  run: IAgentQARunResult;
  dialectCode: string;
  showCitedExamples?: boolean;
}

const AgentQAItemStatus = {
  PENDING: 0,
  PROCESSING: 1,
  DONE: 2,
  ERROR: 3,
} as const;

const SingleResultRenderer: React.FC<SingleResultProps> = ({ run, dialectCode, showCitedExamples = true }) => {
  const { test_result, actual_result, test_metadata, expected_result, status } = run;

  // Check for error status first
  if (status === AgentQAItemStatus.ERROR) {
    const reasoning = test_metadata?.reasoning || 'Unknown error';
    return (
      <ErrorContainer>
        <ErrorBadge>
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <span>ERROR</span>
        </ErrorBadge>
        <ReasoningText>{reasoning}</ReasoningText>
      </ErrorContainer>
    );
  }

  // No result yet (pending or processing)
  if (!test_result) {
    return <PendingText>Pending...</PendingText>;
  }

  // PASS - Show simple green badge (with segment count for dialogs)
  if (test_result === 'pass') {
    let segmentInfo = '';
    try {
      if (actual_result) {
        const actualObj = typeof actual_result === 'string' ? JSON.parse(actual_result) : actual_result;
        if (actualObj.segments && Array.isArray(actualObj.segments)) {
          segmentInfo = ` (${actualObj.segments.length} segments)`;
        }
      }
    } catch (e) {
      // Ignore parse errors
    }

    return (
      <PassBadge>
        <i className="fa fa-check-circle" />
        <span>PASS{segmentInfo}</span>
      </PassBadge>
    );
  }

  // FAIL - Show translation and reasoning with diff view
  if (test_result === 'fail') {
    const reasoning = test_metadata?.reasoning || 'No reasoning provided';

    // Get the actual translation produced by the agent
    let content: JSX.Element | null = null;
    try {
      if (actual_result) {
        const actualObj = typeof actual_result === 'string' ? JSON.parse(actual_result) : actual_result;

        // Check if this is a dialog with segments
        if (actualObj.segments && Array.isArray(actualObj.segments)) {
          content = <FailedDialogSegments actualSegments={actualObj.segments} expectedResult={expected_result} dialectCode={dialectCode} />;
        } else if (actualObj.translation) {
          // Single translation - show diff view
          let expectedTranslation = '';
          try {
            const expectedObj = typeof expected_result === 'string' ? JSON.parse(expected_result) : expected_result;
            expectedTranslation = expectedObj?.translation || '';
          } catch (e) {
            // Ignore parse errors
          }

          const citedExamples = showCitedExamples ? normalizeCitedExamples(actual_result) : [];

          content = (
            <SingleTranslationContainer>
              <DialectLabel>{dialectCode}:</DialectLabel>
              {expectedTranslation ? (
                <>
                  <DiffContainer>
                    <ReactDiffViewer
                      oldValue={expectedTranslation}
                      newValue={actualObj.translation}
                      showDiffOnly
                      compareMethod={DiffMethod.WORDS}
                      splitView={false}
                      hideLineNumbers
                      styles={{
                        wordDiff: {
                          display: 'inline',
                        },
                        diffRemoved: {
                          display: 'none',
                        },
                        diffAdded: {
                          display: 'none',
                        },
                        content: {
                          display: 'block!important',
                          fontSize: 12,
                        },
                        diffContainer: {
                          overflow: 'hidden',
                        },
                        line: {
                          wordBreak: 'break-word',
                        },
                      }}
                    />
                  </DiffContainer>
                  <ExpectedText>Expected: {expectedTranslation}</ExpectedText>
                </>
              ) : (
                <ActualText>{actualObj.translation}</ActualText>
              )}
              {citedExamples.length > 0 && (
                <ReferencedRBs>
                  <ReferencedRBsLabel>Referenced RB:</ReferencedRBsLabel>
                  {citedExamples.map((ex, i) => (
                    <RBItem key={i}>
                      {ex.translation_id > 0 ? `[${ex.translation_id}] ` : ''}{ex.source}
                      {ex.target ? <RBItemTarget> → {ex.target}</RBItemTarget> : null}
                    </RBItem>
                  ))}
                </ReferencedRBs>
              )}
            </SingleTranslationContainer>
          );
        } else {
          content = (
            <FailureMessage>
              <strong>{dialectCode}:</strong> {JSON.stringify(actualObj, null, 2)}
            </FailureMessage>
          );
        }
      }
    } catch (e) {
      content = (
        <FailureMessage>
          <strong>{dialectCode}:</strong> {actual_result || 'N/A'}
        </FailureMessage>
      );
    }

    return (
      <FailContainer>
        <FailBadge>
          <i className="fa fa-times-circle" />
          <span>FAIL</span>
        </FailBadge>

        {content}

        <ReasoningText>{reasoning}</ReasoningText>
      </FailContainer>
    );
  }

  return <PendingText>Unknown</PendingText>;
};

const AgentQAResultColumnRenderer: React.FC<ICellRendererParams> = (params) => {
  if (!params.data) {
    return null;
  }

  const data = params.data as IAgentQAGroupedItem;
  const { runs, pass_count, fail_count, pending_count, error_count, item_info } = data;
  const dialectCode = item_info?.dialect_code || 'XX';
  const totalRuns = runs.length;

  // Single run - render directly without run header
  if (runs.length === 1) {
    return <SingleResultRenderer run={runs[0]} dialectCode={dialectCode} />;
  }

  // Filter runs that need detailed rendering (non-pass)
  const nonPassRuns = runs.filter((run) => run.test_result !== 'pass');

  // Collect cited examples deduped across all non-pass runs
  const allCitedSeen = new Set<string>();
  const allCitedExamples: CitedExample[] = [];
  for (const run of nonPassRuns) {
    for (const ex of normalizeCitedExamples(run.actual_result)) {
      if (!allCitedSeen.has(ex.source)) {
        allCitedSeen.add(ex.source);
        allCitedExamples.push(ex);
      }
    }
  }

  // Multiple runs - show summary and details for non-pass runs
  return (
    <MultiRunContainer>
      <SummaryRow>
        {pass_count > 0 && (
          <SummaryPassBadge>
            <FontAwesomeIcon icon={faCheck} /> {pass_count}
          </SummaryPassBadge>
        )}
        {fail_count > 0 && (
          <SummaryFailBadge>
            <FontAwesomeIcon icon={faTimes} /> {fail_count}
          </SummaryFailBadge>
        )}
        {pending_count > 0 && (
          <SummaryPendingBadge>
            <FontAwesomeIcon icon={faSpinner} /> {pending_count}
          </SummaryPendingBadge>
        )}
        {error_count > 0 && (
          <SummaryErrorBadge>
            <FontAwesomeIcon icon={faExclamationTriangle} /> {error_count}
          </SummaryErrorBadge>
        )}
        <TotalRunsText>/ {totalRuns} runs</TotalRunsText>
      </SummaryRow>

      {nonPassRuns.length > 0 && (
        <RunsList>
          {nonPassRuns.map((run) => (
            <RunItem key={run.id}>
              <RunHeader>Run #{run.run_number}</RunHeader>
              <SingleResultRenderer run={run} dialectCode={dialectCode} showCitedExamples={false} />
            </RunItem>
          ))}
        </RunsList>
      )}
      {allCitedExamples.length > 0 && (
        <ReferencedRBs>
          <ReferencedRBsLabel>Referenced RB:</ReferencedRBsLabel>
          {allCitedExamples.map((ex, i) => (
            <RBItem key={i}>
              {ex.translation_id > 0 ? `[${ex.translation_id}] ${ex.source}` : ex.source}
            </RBItem>
          ))}
        </ReferencedRBs>
      )}
    </MultiRunContainer>
  );
};

export default AgentQAResultColumnRenderer;

// Styled Components

const PendingText = styled.span`
  color: #999;
`;

const PassBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background-color: #e8f5e9;
  border-radius: 4px;

  i {
    color: #4caf50;
  }

  span {
    color: #2e7d32;
    font-weight: 600;
  }
`;

const FailBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background-color: #ffebee;
  border-radius: 4px;
  margin-bottom: 8px;

  i {
    color: #f44336;
  }

  span {
    color: #c62828;
    font-weight: 600;
  }
`;

const FailContainer = styled.div`
  padding: 8px 0;
`;

const ErrorContainer = styled.div`
  padding: 8px 0;
`;

const ErrorBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background-color: #fff3e0;
  border-radius: 4px;
  margin-bottom: 8px;

  svg {
    color: #e65100;
  }

  span {
    color: #e65100;
    font-weight: 600;
  }
`;

const FailureMessage = styled.div`
  color: #d32f2f;
  margin-bottom: 10px;
  line-height: 1.4;
`;

const FailureHeader = styled.div`
  color: #d32f2f;
  margin-bottom: 6px;
  font-weight: 600;
`;

const SegmentContainer = styled.div<{ isLast: boolean }>`
  margin-bottom: ${(props) => (props.isLast ? '10px' : '8px')};
  padding: 6px;
  background-color: #ffebee;
  border-radius: 3px;
`;

const SegmentHeader = styled.div`
  font-size: 11px;
  color: #c62828;
  font-weight: 600;
  margin-bottom: 4px;
`;

const DialectLabel = styled.div`
  font-size: 12px;
  margin-bottom: 4px;

  strong {
    font-weight: 600;
  }
`;

const DiffContainer = styled.div`
  margin-left: 8px;
  margin-bottom: 4px;
  background-color: #fff;
  padding: 6px;
  border-radius: 2px;
`;

const ExpectedText = styled.div`
  font-size: 11px;
  color: #666;
  margin-top: 4px;
`;

const SingleTranslationContainer = styled.div`
  color: #d32f2f;
  margin-bottom: 10px;
  line-height: 1.4;
`;

const ActualText = styled.div`
  margin-left: 8px;
`;

const ReasoningText = styled.div`
  color: #666;
  line-height: 1.5;
  font-style: italic;
`;

// Multi-run styled components
const MultiRunContainer = styled.div`
  padding: 8px 0;
`;

const SummaryRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

const SummaryBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
`;

const SummaryPassBadge = styled(SummaryBadge)`
  background: #e8f5e9;
  color: #2e7d32;
`;

const SummaryFailBadge = styled(SummaryBadge)`
  background: #ffebee;
  color: #c62828;
`;

const SummaryPendingBadge = styled(SummaryBadge)`
  background: #e3f2fd;
  color: #1565c0;
`;

const SummaryErrorBadge = styled(SummaryBadge)`
  background: #fff3e0;
  color: #e65100;
`;

const TotalRunsText = styled.span`
  font-size: 12px;
  color: #999;
`;

const RunsList = styled.div`
  border-left: 2px solid #e0e0e0;
  padding-left: 12px;
`;

const RunItem = styled.div`
  margin-bottom: 12px;
  padding: 8px;
  background: #fafafa;
  border-radius: 4px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const RunHeader = styled.div`
  font-weight: 600;
  font-size: 12px;
  color: #666;
  margin-bottom: 8px;
`;

const ReferencedRBs = styled.div`
  margin-top: 8px;
  padding: 6px 8px;
  background-color: #fff8e1;
  border-left: 3px solid #ffc107;
  border-radius: 2px;
`;

const ReferencedRBsLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #795548;
  margin-bottom: 4px;
`;

const RBItem = styled.div`
  font-size: 11px;
  color: #5d4037;
  padding: 2px 0;
  font-style: italic;
`;

const RBItemTarget = styled.span`
  color: #795548;
  font-style: normal;
`;
